import { toast } from 'react-toastify';

import { getSerializedErrorMessage } from '../../../../utils/errors';
import { finishTreatmentPlanV3Mutation, startTreatmentPlanV3Mutation } from '../../utils';

import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type { TreatmentPlanV3PlanAction, TreatmentPlanV3State } from '../../types';
import type {
  TreatmentPlanV3MedicalThunks,
} from './types';

export function addTreatmentPlanV3MedicalCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3MedicalThunks,
) {
  addGetCases(builder, thunks);
  addInfoCases(builder, thunks);
  addCreateDiagnosisCases(builder, thunks);
  addUpdateDiagnosisCases(builder, thunks);
  addDeleteDiagnosisCases(builder, thunks);
  addCreateSelectionCases(builder, thunks);
  addDeleteSelectionCases(builder, thunks);
}

function isForLoadedPage(state: TreatmentPlanV3State, action: TreatmentPlanV3PlanAction) {
  return action.meta.arg.treatmentPlanId === state.medicalPageTreatmentPlanId;
}

function markPageChanged(state: TreatmentPlanV3State) {
  if (state.medicalPageLoading) state.medicalPageChangedSinceRequest = true;
}

function addGetCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3MedicalThunks,
) {
  const { getTreatmentPlanV3MedicalPage } = thunks;
  builder
    .addCase(getTreatmentPlanV3MedicalPage.pending, (state, action) => {
      if (!isForLoadedPage(state, action)) state.medicalPage = null;
      state.medicalPageLoading = true;
      state.medicalPageError = null;
      state.medicalPageRequestId = action.meta.requestId;
      state.medicalPageTreatmentPlanId = action.meta.arg.treatmentPlanId;
      state.medicalPageChangedSinceRequest = false;
      state.error = null;
    })
    .addCase(getTreatmentPlanV3MedicalPage.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.medicalPageRequestId) return;
      state.medicalPageLoading = false;
      state.medicalPageError = null;
      if (!state.medicalPageChangedSinceRequest || !state.medicalPage) {
        state.medicalPage = action.payload;
      }
      state.medicalPageChangedSinceRequest = false;
    })
    .addCase(getTreatmentPlanV3MedicalPage.rejected, (state, action) => {
      if (action.meta.requestId !== state.medicalPageRequestId) return;
      state.medicalPageLoading = false;
      state.medicalPageError = action.error;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addInfoCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3MedicalThunks,
) {
  const { updateTreatmentPlanV3MedicalInfo } = thunks;
  const flag = 'updateMedicalInfoLoading';
  builder
    .addCase(updateTreatmentPlanV3MedicalInfo.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      updateTreatmentPlanV3MedicalInfo.fulfilled,
      (state, action) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedPage(state, action) || !state.medicalPage) return;
        markPageChanged(state);
        state.medicalPage.medical_info = action.payload;
      },
    )
    .addCase(updateTreatmentPlanV3MedicalInfo.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedPage(state, action)) return;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addCreateDiagnosisCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3MedicalThunks,
) {
  const { createTreatmentPlanV3MedicalDiagnosis } = thunks;
  const flag = 'createMedicalDiagnosisLoading';
  builder
    .addCase(createTreatmentPlanV3MedicalDiagnosis.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      createTreatmentPlanV3MedicalDiagnosis.fulfilled,
      (state, action) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (isForLoadedPage(state, action)) {
          markPageChanged(state);
          state.medicalPage?.diagnoses.push(action.payload);
        }
      },
    )
    .addCase(createTreatmentPlanV3MedicalDiagnosis.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedPage(state, action)) return;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addUpdateDiagnosisCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3MedicalThunks,
) {
  const { updateTreatmentPlanV3MedicalDiagnosis } = thunks;
  const flag = 'updateMedicalDiagnosisLoading';
  builder
    .addCase(updateTreatmentPlanV3MedicalDiagnosis.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      updateTreatmentPlanV3MedicalDiagnosis.fulfilled,
      (state, action) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action) || !isForLoadedPage(state, action)) {
          return;
        }
        const diagnosis = state.medicalPage?.diagnoses.find(
          (row) => row.id === action.payload.id,
        );
        if (diagnosis) {
          markPageChanged(state);
          Object.assign(diagnosis, action.payload);
        }
      },
    )
    .addCase(updateTreatmentPlanV3MedicalDiagnosis.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedPage(state, action)) return;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addDeleteDiagnosisCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3MedicalThunks,
) {
  const { deleteTreatmentPlanV3MedicalDiagnosis } = thunks;
  const flag = 'deleteMedicalDiagnosisLoading';
  builder
    .addCase(deleteTreatmentPlanV3MedicalDiagnosis.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      deleteTreatmentPlanV3MedicalDiagnosis.fulfilled,
      (state, action) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedPage(state, action) || !state.medicalPage) return;
        markPageChanged(state);
        state.medicalPage.diagnoses = state.medicalPage.diagnoses.filter(
          (row) => row.id !== action.meta.arg.diagnosisId,
        );
      },
    )
    .addCase(deleteTreatmentPlanV3MedicalDiagnosis.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedPage(state, action)) return;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addCreateSelectionCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3MedicalThunks,
) {
  const { createTreatmentPlanV3MedicalSelection } = thunks;
  const flag = 'createMedicalSelectionLoading';
  builder
    .addCase(createTreatmentPlanV3MedicalSelection.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      createTreatmentPlanV3MedicalSelection.fulfilled,
      (state, action) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (isForLoadedPage(state, action)) {
          markPageChanged(state);
          state.medicalPage?.selections.push(action.payload);
        }
      },
    )
    .addCase(createTreatmentPlanV3MedicalSelection.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedPage(state, action)) return;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addDeleteSelectionCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3MedicalThunks,
) {
  const { deleteTreatmentPlanV3MedicalSelection } = thunks;
  const flag = 'deleteMedicalSelectionLoading';
  builder
    .addCase(deleteTreatmentPlanV3MedicalSelection.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      deleteTreatmentPlanV3MedicalSelection.fulfilled,
      (state, action) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedPage(state, action) || !state.medicalPage) return;
        markPageChanged(state);
        state.medicalPage.selections = state.medicalPage.selections.filter(
          (row) => row.id !== action.meta.arg.selectionId,
        );
      },
    )
    .addCase(deleteTreatmentPlanV3MedicalSelection.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedPage(state, action)) return;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}
